import { boardProfiles } from './organization.js';

export const SITE_URL = 'https://transformationalwellness.org';
export const SITE_NAME = 'Transformational Wellness';
export const DEFAULT_SOCIAL_IMAGE = '/social/transformational-wellness.jpg';

export const seoByPath = {
  '/': {
    title: 'Transformational Wellness | Healthier Communities, Together',
    description: 'Transformational Wellness unites wellness, education, culture, sustainability, humanitarian action, and economic opportunity to strengthen communities.',
    socialTitle: 'Healthier communities begin together.',
    socialImage: '/social/transformational-wellness.jpg',
    socialImageAlt: 'Transformational Wellness community members gathered beneath the San Francisco skyline',
    pageType: 'WebPage',
    preloadImage: '/assets/hero-community-1600.webp',
    priority: '1.0',
  },
  '/sf': {
    title: 'Transformational Wellness San Francisco | Five Days of Impact',
    description: 'Discover Transformational Wellness San Francisco, April 19-25, 2027: a community experience connecting wellness, education, culture, service, and lasting impact.',
    socialTitle: 'Transformational Wellness San Francisco',
    socialImage: '/social/san-francisco.jpg',
    socialImageAlt: 'Transformational Wellness San Francisco community experience',
    pageType: 'CollectionPage',
    preloadImage: '/assets/hero-community-1600.webp',
    priority: '0.9',
  },
  '/our-story': {
    title: 'Our Story | The K.E.E.N. Movement & Transformational Wellness',
    description: 'Learn how Chanra Hai’s journey from Cambodia to community leadership inspired K.E.E.N. and the Transformational Wellness movement.',
    socialTitle: 'Purpose born from resilience.',
    socialImage: '/social/our-story.jpg',
    socialImageAlt: 'Chanra Hai and the story behind the K.E.E.N. Movement',
    pageType: 'AboutPage',
    priority: '0.8',
  },
  '/board': {
    title: 'Board & Leadership | Transformational Wellness',
    description: 'Meet the board leaders, advisors, program directors, advocates, and visionary partners guiding K.E.E.N. and Transformational Wellness.',
    socialTitle: 'Board & Leadership',
    socialImage: '/social/board-and-leadership.jpg',
    socialImageAlt: 'Board and leadership at Transformational Wellness',
    pageType: 'AboutPage',
    priority: '0.7',
  },
  '/pillars': {
    title: 'The Seven Pillars | Transformational Wellness',
    description: 'Explore the seven pillars of Transformational Wellness: wellness, education, community, sustainability, culture, humanitarian impact, and economic empowerment.',
    socialTitle: 'Seven pillars. One shared future.',
    socialImage: '/social/seven-pillars.jpg',
    socialImageAlt: 'The seven pillars of the Transformational Wellness movement',
    pageType: 'CollectionPage',
    priority: '0.9',
  },
  '/wellness-week': {
    title: 'Transformational Wellness Week | San Francisco',
    description: 'Explore Transformational Wellness Week in San Francisco, April 19-25, 2027, with wellness, family arts, community celebration, and senior connection.',
    socialTitle: 'Five days. One community. Lasting impact.',
    socialImage: '/social/wellness-week.jpg',
    socialImageAlt: 'Transformational Wellness Week in San Francisco',
    pageType: 'CollectionPage',
    preloadImage: '/assets/hero-community-1600.webp',
    priority: '0.9',
  },
  '/project-cambodia': {
    title: 'Project Cambodia | Transformational Wellness',
    description: 'Project Cambodia advances wellness, education, food security, and sustainable community development through the K.E.E.N. Movement.',
    socialTitle: 'Hope today. Impact forever.',
    socialImage: '/social/project-cambodia.jpg',
    socialImageAlt: 'Project Cambodia community wellness, education, and sustainable development',
    pageType: 'WebPage',
    preloadImage: '/assets/project-cambodia-1600.webp',
    priority: '0.9',
  },
  '/partner': {
    title: 'Partner With Purpose | Transformational Wellness',
    description: 'Partner with Transformational Wellness to support community programs, Project Cambodia, and a lasting legacy of health, education, and opportunity.',
    socialTitle: 'Partner with purpose.',
    socialImage: '/social/partner-with-purpose.jpg',
    socialImageAlt: 'Partner with Transformational Wellness to create lasting community impact',
    pageType: 'WebPage',
    priority: '0.8',
  },
  '/contact': {
    title: 'Contact & Get Involved | Transformational Wellness',
    description: 'Connect with Transformational Wellness to volunteer, participate in Wellness Week, bring the movement to your city, or explore partnership.',
    socialTitle: 'Join the movement.',
    socialImage: '/social/join-the-movement.jpg',
    socialImageAlt: 'Join the Transformational Wellness movement',
    pageType: 'ContactPage',
    priority: '0.8',
  },
  '/fiscal-sponsor': {
    title: 'Fiscal Sponsorship | Transformational Wellness',
    description: 'Learn how Power of One Foundation manages donations and event support for Transformational Wellness Week San Francisco, April 19-25, 2027.',
    socialTitle: 'Giving that supports the mission.',
    socialImage: '/social/partner-with-purpose.jpg',
    socialImageAlt: 'Fiscal sponsorship and giving for Transformational Wellness Week San Francisco',
    pageType: 'WebPage',
    priority: '0.5',
  },
};

export const routeLabels = {
  '/': 'Home',
  '/sf': 'San Francisco',
  '/our-story': 'Our Story',
  '/board': 'Board & Leadership',
  '/pillars': 'The Seven Pillars',
  '/wellness-week': 'Wellness Week',
  '/project-cambodia': 'Project Cambodia',
  '/partner': 'Partner',
  '/contact': 'Contact',
  '/fiscal-sponsor': 'Fiscal Sponsor',
};

export const notFoundSeo = {
  title: 'Page Not Found | Transformational Wellness',
  description: 'The requested page could not be found.',
  socialTitle: 'Transformational Wellness',
  socialImage: DEFAULT_SOCIAL_IMAGE,
  socialImageAlt: 'Transformational Wellness',
  pageType: 'WebPage',
  noindex: true,
};

export function canonicalUrl(path) {
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

export function seoForPath(path) {
  return seoByPath[path] || notFoundSeo;
}

export function structuredDataForPath(path) {
  const seo = seoForPath(path);
  const url = canonicalUrl(path);
  const organizationId = `${SITE_URL}/#organization`;
  const websiteId = `${SITE_URL}/#website`;
  const graph = [
    {
      '@type': 'Organization',
      '@id': organizationId,
      name: SITE_NAME,
      alternateName: 'K.E.E.N. Transformational Wellness',
      url: `${SITE_URL}/`,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/icons/icon-512.png`,
        width: 512,
        height: 512,
      },
      image: `${SITE_URL}${DEFAULT_SOCIAL_IMAGE}`,
      description: seoByPath['/'].description,
      founder: {
        '@type': 'Person',
        '@id': `${SITE_URL}/board#chanra-hai`,
        name: 'Chanra Hai',
        jobTitle: 'Founder, Chairwoman and Chief Executive Officer',
      },
      member: boardProfiles
        .filter((person) => person.id !== 'chanra-hai')
        .map((person) => ({ '@id': `${SITE_URL}/board#${person.id}` })),
      areaServed: [
        { '@type': 'City', name: 'San Francisco' },
        { '@type': 'Country', name: 'Cambodia' },
      ],
      knowsAbout: [
        'Wellness',
        'Education',
        'Community development',
        'Sustainability',
        'Culture',
        'Humanitarian impact',
        'Economic empowerment',
      ],
    },
    {
      '@type': 'WebSite',
      '@id': websiteId,
      url: `${SITE_URL}/`,
      name: SITE_NAME,
      alternateName: 'K.E.E.N. Transformational Wellness',
      publisher: { '@id': organizationId },
      inLanguage: 'en-US',
    },
    {
      '@type': seo.pageType,
      '@id': `${url}#webpage`,
      url,
      name: seo.title,
      description: seo.description,
      isPartOf: { '@id': websiteId },
      about: { '@id': organizationId },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: `${SITE_URL}${seo.socialImage}`,
        width: 1200,
        height: 630,
      },
      inLanguage: 'en-US',
    },
  ];

  if (path !== '/' && seoByPath[path]) {
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${SITE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: routeLabels[path],
          item: url,
        },
      ],
    });
  }

  if (path === '/board') {
    graph.push(...boardProfiles.map((person) => ({
      '@type': 'Person',
      '@id': `${SITE_URL}/board#${person.id}`,
      name: person.name,
      jobTitle: person.role,
      description: person.bio[0],
      image: `${SITE_URL}${person.image}`,
      memberOf: { '@id': organizationId },
    })));
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
